<template>
  <p
    class="price-value tw-flex tw-items-baseline"
  >
    <span
      v-for="(part, index) in priceParts"
      :key="index"
      :class="part.class"
    >
      {{ part.value }}
    </span>
  </p>
</template>

<script>
  import { defineComponent, computed } from '@vue/composition-api'
  import useI18n from '@/composables/useI18n'

  /**
   * Displays formatted amount value with proper currency formatting and styling for small cents
   * @module component - PriceSmallCents
   * @param {number} amount - Price amount in cents
   * @param {string} currency - Currency code (ISO 4217)
   * @param {boolean} round - Whether to round to whole numbers (no decimals)
   * @param {string} locale - Optional locale override
   */
  export default defineComponent({
    name: 'PriceSmallCents',
    props: {
      amount: {
        type: Number,
        required: true
      },
      currency: {
        type: String,
        required: true
      },
      round: {
        type: Boolean,
        default: false
      },
      locale: {
        type: String,
        default: undefined
      }
    },
    setup (props) {
      const i18n = useI18n()

      function formatCurrencyParts (number, currency, locale, round = false) {
        if (number === undefined) throw new Error('`number` is required.')
        if (currency === undefined) throw new Error('`currency` is required.')
        if (locale === undefined) throw new Error('`locale` is required.')
        const numberToFormat = number / 100
        const decimals = round ? 0 : 2

        const formatter = new Intl.NumberFormat(locale, {
          style: 'currency',
          currency,
          minimumFractionDigits: decimals
        })

        const parts = formatter.formatToParts(numberToFormat)

        return parts.map((part) => {
          if (part.type === 'fraction') {
            return {
              ...part,
              class: 'tw-text-base' // smaller cents
            }
          }
          return part
        })
      }

      const priceParts = computed(() => {
        const locale = props.locale || i18n.value.locale
        return formatCurrencyParts(
          props.amount,
          props.currency,
          locale,
          props.round
        )
      })

      return {
        priceParts
      }
    }
  })
</script>
